import { useVirtualizer } from '@tanstack/react-virtual'
import FbButton from 'components/FbUI/FbButton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import { cn } from 'components/UI/cn'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Form } from 'react-bootstrap'
import Checkbox from 'components/Checkbox/checkbox'

import { FilterButton } from 'components/Filters/components/FilterButton'
import { FilterCloseButton } from 'components/Filters/components/FilterCloseButton'
import { useEventHistoryFilterStore } from '../EventHistoryFilterStore'
import { useUsers } from 'services/users'

interface TeamMemberOption {
  value: number
  label: string
}

interface TeamMemberFilterProps {
  /** When true, save/clear commit directly to the applied filter state.
   *  Use for standalone usage (no outer modal Apply button gating commit). */
  inline?: boolean
}

export function TeamMemberFilter({
  inline = false,
}: TeamMemberFilterProps = {}) {
  const { data: users } = useUsers()

  const { pendingTeamMemberIds, setPendingTeamMemberIds, applyFilters } =
    useEventHistoryFilterStore()
  const isActive = !!pendingTeamMemberIds.length

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [internalFilters, setInternalFilters] =
    useState<number[]>(pendingTeamMemberIds)

  // Convert users to options
  const options: TeamMemberOption[] = useMemo(() => {
    if (!users) return []
    return users.map((user) => ({
      value: user.id,
      label: `${user.first_name} ${user.last_name}`.trim() || user.email,
    }))
  }, [users])

  const filteredOptions = options
    .filter((o) => {
      const regex = new RegExp(search, 'gi')
      const labelTest = regex.test(o.label)
      const isSelected = internalFilters.includes(o.value)
      return isSelected || labelTest
    })
    .sort((a, b) => {
      const isASelected = internalFilters.includes(a.value) ? 1 : -1
      const isBSelected = internalFilters.includes(b.value) ? 1 : -1
      return isBSelected - isASelected
    })

  const activeCount = pendingTeamMemberIds?.length ?? 0

  function handleInternalChange(option: TeamMemberOption, selected: boolean) {
    if (selected) {
      setInternalFilters((prev) => prev.filter((f) => f !== option.value))
    } else {
      setInternalFilters((prev) => [...prev, option.value])
    }
  }

  function selectAll() {
    setInternalFilters(options.map((o) => o.value))
  }

  function selectNone() {
    setInternalFilters([])
  }

  function clearFilters() {
    setPendingTeamMemberIds([])
    if (inline) applyFilters()
  }

  function saveFilters() {
    setPendingTeamMemberIds(internalFilters)
    if (inline) applyFilters()
  }

  // Set by `cancelFilters` to suppress the save-on-close behavior for that
  // single close event. Cleared each time `handleOpenChange` runs.
  const skipSaveOnNextClose = useRef(false)

  function cancelFilters() {
    skipSaveOnNextClose.current = true
    setInternalFilters(pendingTeamMemberIds)
    setOpen(false)
    // Defense-in-depth: Radix in controlled mode does not re-fire
    // onOpenChange when we toggle `open` ourselves, so handleOpenChange
    // never gets a chance to clear the flag. Reset inline to keep the
    // guard scoped to this single cancel event.
    skipSaveOnNextClose.current = false
  }

  // Syncs the internal filters with the filters in the store
  useEffect(() => {
    setInternalFilters(pendingTeamMemberIds)
  }, [pendingTeamMemberIds, open])

  function handleOpenChange(open: boolean) {
    if (!open && !skipSaveOnNextClose.current) {
      // Apply filters when the dropdown closes (e.g. click-outside).
      saveFilters()
    }
    skipSaveOnNextClose.current = false
    setOpen(open)
  }

  if (!options.length) return null

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <div className={'relative d-flex'}>
        <DropdownMenuTrigger asChild>
          <FilterButton
            open={open}
            title="Team Member"
            isActive={isActive}
            count={activeCount}
          />
        </DropdownMenuTrigger>
        <FilterCloseButton
          isActive={isActive}
          open={open}
          onClick={() => clearFilters()}
        />
      </div>

      <DropdownMenuContent
        forceMount={true}
        side={'bottom'}
        // The TeamMember trigger sits at the right end of the filter row on
        // the event-history pages; aligning the popover's right edge to the
        // trigger's keeps the 280px content inside the viewport instead of
        // drifting off the right edge.
        align="end"
        collisionPadding={12}
        className={cn(
          '!rounded-xl !border-neutral-200 !shadow-lg !bg-white w-[280px] lg:w-[260px] !p-3'
        )}
        sideOffset={6}
      >
        <div className="flex items-center justify-between gap-2 pb-2">
          <div
            className="flex items-center gap-2 cursor-pointer min-w-0"
            onClick={() => {
              if (internalFilters.length === filteredOptions.length) {
                selectNone()
              } else {
                selectAll()
              }
            }}
          >
            <Checkbox
              checked={internalFilters.length === filteredOptions.length}
              indeterminate={
                internalFilters.length > 0 &&
                internalFilters.length < filteredOptions.length
              }
              readOnly
              wrapperClassName="flex-shrink-0 pointer-events-none"
            />
            <span className="text-sm font-semibold text-neutral-800 truncate">
              Select all
            </span>
          </div>
          <button
            type="button"
            onClick={() => selectNone()}
            disabled={internalFilters.length === 0}
            className={cn(
              'text-sm font-medium transition-colors',
              internalFilters.length === 0
                ? 'text-neutral-400 cursor-not-allowed'
                : 'text-brand-active hover:text-brand-bright'
            )}
          >
            Reset
          </button>
        </div>
        <hr className="border-0 border-t border-neutral-200 -mx-3 mb-2" />

        <Form.Control
          type="text"
          onChange={(e) => {
            setSearch(e.target.value)
          }}
          placeholder="Search"
          className="!text-sm !border-neutral-300 !rounded-lg"
        />

        <ItemsList
          options={filteredOptions}
          onSelect={handleInternalChange}
          selected={internalFilters}
        />

        <div className="mt-3 flex items-center gap-2">
          <FbButton
            size="sm"
            variant="secondary"
            type="button"
            className="flex-1"
            onClick={cancelFilters}
          >
            Cancel
          </FbButton>
          <FbButton
            size="sm"
            type="button"
            className="flex-1 !bg-brand-bright !text-white hover:!bg-brand-active active:!bg-brand-active disabled:!bg-neutral-100 disabled:!text-neutral-400 disabled:!border-neutral-100 disabled:hover:!bg-neutral-100"
            onClick={() => {
              saveFilters()
              setOpen(false)
            }}
          >
            Apply
          </FbButton>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function ItemsList(props: {
  options: TeamMemberOption[]
  selected: number[]
  onSelect: (option: TeamMemberOption, selected: boolean) => void
}) {
  const parentRef = useRef<HTMLDivElement>(null)

  const virtual = useVirtualizer({
    count: props.options?.length ?? 1,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 35,
  })

  return (
    <div
      className={'mt-2 w-full'}
      ref={parentRef}
      style={{
        height: `150px`,
        width: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <div
        style={{
          height: `${virtual.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
          contain: 'layout style paint',
          willChange: 'auto',
        }}
      >
        {virtual.getVirtualItems().map((item) => {
          const o = props.options[item.index]
          const isSelected = props.selected.includes(o?.value)
          if (!o) return null
          return (
            <div
              key={item.index}
              className={cn(
                'max-w-full cursor-pointer flex items-center py-2 px-2 gap-2 rounded-lg hover:bg-neutral-100 text-sm text-neutral-700 overflow-ellipsis whitespace-nowrap'
              )}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${item.size}px`,
                transform: `translateY(${item.start}px)`,
              }}
              onClick={() => {
                props.onSelect(o, isSelected)
              }}
            >
              <Checkbox
                checked={isSelected}
                readOnly
                wrapperClassName="flex-shrink-0 pointer-events-none"
              />
              <div
                className={
                  'overflow-ellipsis overflow-hidden whitespace-nowrap'
                }
              >
                {o.label}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
